/**
 * Created by Mateusz Lipowski on 27.01.2017.
 */

namespace Entity.Map.Marker {
    export abstract class Marker extends Entity.Map.MapEntity {

        //protected static ICON_PATH = 'https://chart.apis.google.com/chart?';
        protected static ICON_DRAG = '/img/marker-drag.png';

        // Teardrop pin drawn as a vector SVG path: tip at (0,0), round head
        // centred at (0,-30). Replaces the old Google Chart API pin images
        // (chart.apis.google.com, shut down) so pins keep their per-node colour.
        protected static PIN_PATH = 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z';

        protected chst: string = 'd_map_spin';
        protected chld: string = '';
        protected defaultColor: string = 'FFFFFF';

        protected _marker: google.maps.Marker;
        protected _map: google.maps.Map;
        protected _lat: number;
        protected _lng: number;
        protected _isVisible: boolean = true;

        public constructor(lat: number, lng: number, map: google.maps.Map) {
            super();
            this._lat = lat;
            this._lng = lng;
            this._map = map;
        }

        public setMap(map:google.maps.Map):void {
            this._map = map;
            if(this._marker) {
                this._marker.setMap(map);
            }
        }

        public getPosition():string {
            return `${this._lat},${this._lng}`;
        }

        public getPositionObject():google.maps.LatLng {
            return new google.maps.LatLng(this._lat, this._lng);
        }

        public toggleVisibility() {
            if(!this._marker)
                return;

            if (this._marker.getMap() != null)
                this._marker.setMap(null);
            else
                this._marker.setMap(this._map);
        }

        public determineVisibility() {
            if(!this._marker || !this._map)
                return;

            this.show();
        }

        protected onClick(params): void {

        }

        protected show(): void {
            this._marker.setVisible(true);
            this._isVisible = true;
        }

        protected hide(): void {
            this._marker.setVisible(false);
            this._isVisible = false;
        }

        protected initializeMarker():void {
            this._marker = new google.maps.Marker({
                position: this.getPositionObject(),
                icon: this.getIconObject(),
            });

            this._marker.addListener('click', (params) => {
                App.getInstance().service.callbackContainer.invokeCallbacks(Control.CallbackContainer.CallbackSource.MARKER, new Control.CallbackContainer.Event('click', this, params));
                this.onClick(params);
            });

            this._marker.addListener('rightclick', (params) => {
                App.getInstance().service.callbackContainer.invokeCallbacks(Control.CallbackContainer.CallbackSource.MARKER, new Control.CallbackContainer.Event('rightclick', this, params));
            });

            this._marker.addListener('drag', (params) => {
                App.getInstance().service.callbackContainer.invokeCallbacks(Control.CallbackContainer.CallbackSource.MARKER, new Control.CallbackContainer.Event('drag', this, params));
            });

            this._marker.addListener('dragend', (params) => {
                App.getInstance().service.callbackContainer.invokeCallbacks(Control.CallbackContainer.CallbackSource.MARKER, new Control.CallbackContainer.Event('dragend', this, params));
            });

            this.body = this._marker;
            this.setupInputEvent(InputEvent.RightClick, (params => {
                App.getInstance().service.contextMenu.show(this, new Action.UserActionEvent(this, params, null));
            }));
        }

        // Re-applies the marker's icon (call after color/symbol change).
        protected refreshMarkerIcon(): void {
            if(!this._marker)
                return;

            this._marker.setIcon(this.getIconObject());
        }

        // Cache of pin icons keyed by "fillColor|label". With ~11k markers but
        // only a few dozen distinct colours, every marker of the same colour and
        // label shares one raster image resource — cheap, like the old single PNG.
        protected static iconCache: {[key: string]: Object} = {};

        protected getIconObject(): Object {
            let fill: string = this.getFillColor();
            let label: string = this.getLabelText();
            let key: string = fill + '|' + label;

            if(!Marker.iconCache[key])
                Marker.iconCache[key] = Marker.buildPinIcon(fill, label);

            return Marker.iconCache[key];
        }

        // Builds a teardrop pin as a self-contained SVG data-URI image, with the
        // colour and (optional) label baked in. Using an image icon instead of a
        // google.maps.Symbol + MarkerLabel keeps rendering cheap at scale.
        protected static buildPinIcon(fill: string, label: string): Object {
            let text: string = '';
            if(label) {
                let textColor: string = Marker.labelTextColor(fill);
                text = '<text x="0" y="-29" text-anchor="middle" dominant-baseline="central"'
                    + ' font-family="Arial, sans-serif" font-size="12" font-weight="bold"'
                    + ' fill="' + textColor + '">' + Marker.escapeXml(label) + '</text>';
            }

            let svg: string = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="44" viewBox="-12 -42 24 44">'
                + '<path d="' + Marker.PIN_PATH + '" fill="' + fill + '" stroke="#000000" stroke-width="1"/>'
                + text
                + '</svg>';

            return {
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
                anchor: new google.maps.Point(12, 42),
                scaledSize: new google.maps.Size(24, 44)
            };
        }

        // Fill colour for the pin. Node colours are stored as bare hex
        // (e.g. "FF0000"), so prepend '#' for a valid CSS colour.
        protected getFillColor(): string {
            let hex: string = this.color || this.defaultColor;
            return (hex.charAt(0) === '#') ? hex : '#' + hex;
        }

        // The short text baked into the pin (e.g. type symbol). Empty = no label.
        protected getLabelText(): string {
            return '';
        }

        protected static escapeXml(value: string): string {
            return value
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
        }

        // Black or white label text, whichever contrasts with the pin colour.
        protected static labelTextColor(fillHex: string): string {
            let hex: string = fillHex.replace('#', '');
            if(hex.length === 3)
                hex = hex.charAt(0) + hex.charAt(0) + hex.charAt(1) + hex.charAt(1) + hex.charAt(2) + hex.charAt(2);

            let r: number = parseInt(hex.substring(0, 2), 16);
            let g: number = parseInt(hex.substring(2, 4), 16);
            let b: number = parseInt(hex.substring(4, 6), 16);
            let luminance: number = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

            return (luminance > 0.6) ? '#000000' : '#FFFFFF';
        }

        protected getIconPath(): string {
            return '/img/pin_icon.png';
        }

        get color(): string {
            return this.defaultColor;
        }

        get marker(): google.maps.Marker {
            return this._marker;
        }
    }
}